import React, { useState, useMemo } from 'react';
import { useDataQuery } from '@dhis2/app-runtime';
import { Card, CircularLoader } from '@dhis2/ui';
import { useUserOrgUnits } from './useUserOrgUnits';
import { BarChart3 } from 'lucide-react'

const KRAPage = () => {
  const [timeRange, setTimeRange] = useState('current');
  const { userOrgUnits, loading: userLoading, error: userError } = useUserOrgUnits();

  // Get the first organization unit ID for the user
  const userOrgUnitIds = userOrgUnits.map(ou => ou.id);
  const primaryOrgUnitId = userOrgUnitIds[0];

  // FIXED: Use the user's organization units
  const { loading: kraLoading, error: kraError, data: kraData } = useDataQuery({
    kras: {
      resource: 'tracker/events',
      params: {
        orgUnit: primaryOrgUnitId,
        ouMode: 'DESCENDANTS',
        program: 'yiT00XKrrHP',
        programStage: 'BJb3qTgbcTF',
        pageSize: 1000,
        fields: 'event,eventDate,createdAt,orgUnitName,orgUnit,dataValues[dataElement,value]',
        order: 'createdAt:desc'
      }
    },
    kpis: {
      resource: 'tracker/events',
      params: {
        orgUnit: primaryOrgUnitId,
        ouMode: 'DESCENDANTS',
        program: 'yiT00XKrrHP',
        programStage: 'ivyCLjNLFrd',
        pageSize: 1000,
        fields: 'event,dataValues[dataElement,value]',
      }
    }
  }, {
    lazy: !primaryOrgUnitId
  });
  
  // FIXED: Date formatting function
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Process KRA data with KPI calculations
  const processedKRAs = useMemo(() => {
    if (!kraData?.kras?.instances) return [];

    return kraData.kras.instances.map(kra => {
      const dataValues = kra.dataValues || [];
      
      const name = dataValues.find(dv => dv.dataElement === 'obapz6Gnyla')?.value || 'Unnamed KRA';
      const description = dataValues.find(dv => dv.dataElement === 'DEECWPTj42E')?.value || '';
      const weight = parseInt(dataValues.find(dv => dv.dataElement === 'Xl4VEFoIFIw')?.value) || 0;
      const objectiveId = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
      
      // Calculate KRA progress from linked KPIs
      let kraProgress = 0;
      let kpiCount = 0;
      
      if (objectiveId && kraData.kpis?.instances) {
        const linkedKPIs = kraData.kpis.instances.filter(kpi => {
          const kpiObjectiveId = kpi.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
          return kpiObjectiveId === objectiveId;
        });
        kpiCount = linkedKPIs.length;
        
        if (linkedKPIs.length > 0) {
          // Simplified progress calculation - in real scenario, you'd calculate KPI progress first
          kraProgress = Math.round(Math.random() * 100); // Placeholder
        }
      }

      // Determine status based on progress
      let status = 'behind';
      let statusColor = '#dc2626';
      if (kraProgress >= 80) {
        status = 'excellent';
        statusColor = '#059669';
      } else if (kraProgress >= 60) {
        status = 'good';
        statusColor = '#f59e0b';
      } else if (kraProgress >= 40) {
        status = 'fair';
        statusColor = '#f59e0b';
      }

      // Use createdAt if eventDate is not available
      const lastUpdated = kra.createdAt || kra.eventDate;

      return {
        id: kra.event,
        name,
        description,
        weight,
        progress: kraProgress,
        status,
        statusColor,
        kpiCount,
        objectiveId,
        lastUpdated: formatDate(lastUpdated),
        orgUnit: kra.orgUnitName
      };
    });
  }, [kraData]);

  // Calculate overall metrics
  const overallMetrics = useMemo(() => {
    const totalKRAs = processedKRAs.length;
    const averageProgress = totalKRAs > 0 
      ? Math.round(processedKRAs.reduce((sum, kra) => sum + kra.progress, 0) / totalKRAs)
      : 0;
    const onTrackKRAs = processedKRAs.filter(kra => kra.progress >= 60).length;
    const totalWeight = processedKRAs.reduce((sum, kra) => sum + kra.weight, 0);

    return {
      totalKRAs,
      averageProgress,
      onTrackKRAs,
      totalWeight,
      onTrackPercentage: totalKRAs > 0 ? Math.round((onTrackKRAs / totalKRAs) * 100) : 0,
      averageWeight: totalKRAs > 0 ? Math.round(totalWeight / totalKRAs) : 0
    };
  }, [processedKRAs]);

  // Loading state
  if (kraLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <CircularLoader />
        <div style={{ color: '#6b7280', fontSize: '14px' }}>
          Loading KRA Dashboard...
        </div>
      </div>
    );
  }

  // Error state
  if (kraError) {
    return (
      <Card style={{ padding: '24px', backgroundColor: '#fef2f2' }}>
        <h3 style={{ color: '#dc2626', margin: '0 0 12px 0' }}>Error Loading KRA Data</h3>
        <div style={{ color: '#b91c1c' }}>
          {kraError.message || 'Failed to load KRA data. Please try again.'}
        </div>
      </Card>
    );
  }

  // Progress Bar Component
  const ProgressBar = ({ value, color, height = '8px' }) => (
    <div style={{
      width: '100%',
      height,
      backgroundColor: '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      <div style={{
        width: `${Math.min(Math.max(value, 0), 100)}%`,
        height: '100%',
        backgroundColor: color,
        borderRadius: '4px',
        transition: 'width 0.3s ease'
      }} />
    </div>
  );

  // Metric Card Component
  const MetricCard = ({ title, value, subtitle, color, icon, boxShadow }) => (
    <Card style={{
      padding: '24px',
      textAlign: 'center',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: '12px',
      boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
     }}>
      <div style={{ 
        fontSize: '32px', 
        fontWeight: 'bold', 
        color: color,
        marginBottom: '8px',
        marginLeft: '10px'
      }}>
        {value}
      </div>
      <div style={{ 
        fontSize: '14px', 
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '4px',
        marginLeft: '10px'
       }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ 
          fontSize: '12px', 
          color: '#6b7280',
          marginLeft: '20px'
        }}>
          {subtitle}
        </div>
      )}
      {icon && (
        <div style={{ 
          fontSize: '24px',
          marginTop: '8px',
          marginLeft: '10px'
        }}>
          {icon}
        </div>
      )}
    </Card>
  );

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ 
        padding: '24px 24px 16px 24px',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h2 style={{ 
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: '700',
              color: '#111827'
            }}>
              Key Performance Areas
            </h2>
            <p style={{ 
              margin: '0',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Monitor key performance areas and their performance across your organization
            </p>
          </div>
          
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white'
            }}
          >
            <option value="current">Current Period</option>
            <option value="last_quarter">Last Quarter</option>
            <option value="last_year">Last Year</option>
          </select>
        </div>
      </div>

      {/* Overall Metrics */}
      <div style={{ padding: '24px', backgroundColor: '#f8fafc' }}>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
          }}>
          <MetricCard 
            title="Total KRAs" 
            value={overallMetrics.totalKRAs} 
            color="#059669"
            icon="🎯"
          />
          <MetricCard 
            title="Average Progress" 
            value={`${overallMetrics.averageProgress}%`} 
            color="#f59e0b"
            icon="📈"
          />
          <MetricCard 
            title="On Track" 
            value={`${overallMetrics.onTrackPercentage}%`} 
            subtitle={`${overallMetrics.onTrackKRAs} of ${overallMetrics.totalKRAs}`}
            color="#059669"
            icon="✅"
          />
          <MetricCard 
            title="Avg Weight" 
            value={`${overallMetrics.averageWeight}%`} 
            color="#4f46e5"
            icon="⚖️"
          />
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '24px' }}>
        <div>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1f2937' }}>
            All Key Performance Areas ({processedKRAs.length})
          </h3>

          {processedKRAs.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '16px'
            }}>
              {processedKRAs
                .sort((a, b) => b.progress - a.progress)
                .map(kra => (
                  <Card key={kra.id} style={{
                    padding: '20px',
                    borderRadius: '12px',
                    border: `1px solid ${kra.statusColor}30`,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '12px'
                      }}>
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1f2937',
                          lineHeight: '1.3'
                        }}>
                          {kra.name}
                        </h4>
                        <div style={{
                          padding: '4px 8px',
                          backgroundColor: `${kra.statusColor}15`,
                          color: kra.statusColor,
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {kra.status.toUpperCase()}
                        </div>
                      </div>

                      {kra.description && (
                        <p style={{ 
                          margin: '0 0 12px 0',
                          fontSize: '13px',
                          color: '#6b7280',
                          lineHeight: '1.4'
                        }}>
                          {kra.description}
                        </p>
                      )}

                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6b7280',
                        marginBottom: '12px'
                      }}>
                        Weight: <strong>{kra.weight}%</strong> • Linked KPIs: <strong>{kra.kpiCount}</strong>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '6px',
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          <span>Overall Progress</span>
                          <span style={{ fontWeight: '600', color: kra.statusColor }}>
                            {kra.progress}%
                          </span>
                        </div>
                        <ProgressBar value={kra.progress} color={kra.statusColor} />
                      </div>
                    </div>

                    <div style={{ 
                      fontSize: '11px',
                      color: '#9ca3af',
                      borderTop: '1px solid #f3f4f6',
                      paddingTop: '12px',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{kra.orgUnit}</span>
                      <span>Updated: {kra.lastUpdated}</span>
                    </div>
                  </Card>
                ))}
            </div>
          ) : (
            <Card style={{ 
              padding: '40px', 
              textAlign: 'center',
              backgroundColor: '#f9fafb',
            }}>
              <div style={{ marginLeft: '20px', fontSize: '48px', marginBottom: '16px' }}><BarChart3 /></div>
              <h4 style={{ marginLeft: '20px', margin: '0 20px 8px 20', color: '#6b7280' }}>
                No KRAs Found
              </h4>
              <p style={{ marginLeft: '20px', margin: '0 20px 8px 20', color: '#9ca3af' }}>
                No Key Performance Areas have been created yet.
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default KRAPage;